import type { ClientPreferencesComponentInput, ComponentValueResponse } from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as clientPreferencesApi from "@/lib/api/flow-instances/client-preferences";
import { useInvalidateFlowCompletion } from "./use-flow-completion";
import { useInvalidateDynamicSync } from "./use-portfolios";

export const clientPreferencesKeys = {
  flowInstanceValue: (id: string | null) =>
    ["flowInstances", id, "client-preferences", "flow-instance-value"] as const,
};

export function useClientPreferencesFlowInstanceValue(id: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: clientPreferencesKeys.flowInstanceValue(id),
    queryFn: () => clientPreferencesApi.getFlowInstanceValue(id!),
    enabled: !!id,
  });
  return {
    inputData: data?.inputData ?? null,
    calculatedData: data?.calculatedData ?? null,
    error,
    isLoading,
  };
}

export function useSaveClientPreferences(instanceId: string) {
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();
  const invalidateDynamicSync = useInvalidateDynamicSync();
  const queryKey = clientPreferencesKeys.flowInstanceValue(instanceId);
  // Every save carries the full answers map (and regenerates dynamic portfolios), so
  // saves run one at a time in click order (`scope`) and only the newest may write the
  // cache — a superseded save's reply or rollback would wipe the newer answers. A queued
  // save counts as pending, as does the settling one inside its own callbacks, so `> 1`
  // means a newer save is waiting.
  const isSuperseded = () => queryClient.isMutating({ mutationKey: queryKey }) > 1;
  return useMutation({
    mutationKey: queryKey,
    scope: { id: `client-preferences:${instanceId}` },
    mutationFn: (input: ClientPreferencesComponentInput) =>
      clientPreferencesApi.saveFlowInstanceValue(instanceId, input),
    // Optimistic input echo — the widget re-initializes from this cache on
    // remount (wizard tab switches), mirroring the sustainability hook.
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData<ComponentValueResponse>(queryKey);
      queryClient.setQueryData<ComponentValueResponse>(queryKey, (old) => ({
        inputData: JSON.stringify(input),
        calculatedData: old?.calculatedData ?? null,
      }));
      return { previousData };
    },
    onError: (_err, _input, context) => {
      if (context?.previousData !== undefined && !isSuperseded()) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSuccess: (result) => {
      if (result.success && result.inputData !== undefined) {
        if (!isSuperseded()) {
          queryClient.setQueryData(queryKey, {
            inputData: result.inputData,
            calculatedData: result.calculatedData,
          });
        }
        // The component gates Submit (every preference must be answered).
        invalidateCompletion(instanceId);
        // QP-185 — the save regenerated dynamic portfolios.
        invalidateDynamicSync(result.dynamicSync);
      }
    },
  });
}
